/**
 * Seed palette — the only place raw hex values may appear in this repository.
 *
 * Every value below was measured from the approved artwork by decoding the
 * source PNGs and averaging large flat regions, then reconciled into a single
 * canonical value per role. The designs are raster renders, so the same
 * surface varies slightly between screens (the primary CTA sampled #02501F on
 * "Thank You" and #076320 on "Home"). Those variations are rendering noise,
 * not design intent, so each role collapses to one token here.
 *
 * See docs/DESIGN_TOKENS.md for the measurement method and the raw samples.
 *
 * DO NOT add a colour here that can be expressed as an existing seed at an
 * opacity step — add the opacity step in `alpha.ts` instead.
 */

export const seed = {
  /**
   * Brand deep green. Impact footer band, primary CTAs, splash background,
   * active toggles, section headings.
   * Mean of 8 samples across Home, Thank You, How It Works, My Donations,
   * Profile, Distribution Proof and the splash screen.
   */
  green: '#04531F',

  /**
   * Brand bright green. Success marks, completed check circles, and the tint
   * source for every light-green surface in the product.
   * Mean of 5 samples; light pills measured as this colour at 20-25% opacity.
   */
  greenBright: '#3F9D3A',

  /**
   * Brand orange. Logo heart and ring, "Meals Distributed" notification icon,
   * and the admin "Preparing" status pill.
   */
  orange: '#FA9008',

  /** Warm cream page background used on every donor screen. */
  cream: '#FBF6F0',

  /** Card and header surface. */
  white: '#FFFFFF',

  /**
   * Text base. All type colours are this ink at an opacity step rather than
   * separate grey values.
   */
  ink: '#16130F',

  /** Admin sidebar. Sampled from a large flat region of the admin dashboard. */
  navy: '#1A2530',

  /** Informational / "Accepted" status. */
  blue: '#0E5C99',

  /** Destructive: logout, cancelled donations, payment failure. */
  red: '#B3261E',
} as const;

export type SeedToken = keyof typeof seed;

/**
 * Brand assets that are colours but not theme roles. Kept separate so a theme
 * swap never accidentally recolours the logo.
 */
export const brandFixed = {
  logoGreen: seed.green,
  logoOrange: seed.orange,
} as const;
